import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { Save, RefreshCw, FileText, Users, Plus, Trash2, Download, Eye } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import ActivationBriefingCard from '../member/ActivationBriefingCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * BriefingManagerTab
 * ------------------
 * Admin tab for the Activation Briefing system:
 *   - Edit version, title, intro, sections, acknowledgments, copy
 *   - View acknowledgments (who/when/version/ip)
 *   - Export-ready (JSON view)
 */
const BriefingManagerTab = () => {
  const { token } = useAuth();
  const auth = { Authorization: `Bearer ${token}` };

  const [cfg, setCfg] = useState(null);
  const [stats, setStats] = useState(null);
  const [acks, setAcks] = useState([]);
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState('edit'); // edit | acks
  const [previewOpen, setPreviewOpen] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/admin/waitlist/briefing`, { headers: auth });
      setCfg(r.data.config);
      setStats(r.data.stats);
      const r2 = await axios.get(`${API}/admin/waitlist/briefing/acknowledgments?limit=200`, { headers: auth });
      setAcks(r2.data.items || []);
    } catch (e) {
      toast.error('Failed to load briefing config');
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    if (!cfg) return;
    setSaving(true);
    try {
      const payload = {
        version: cfg.version,
        title: cfg.title,
        subtitle: cfg.subtitle,
        intro: cfg.intro,
        sections: cfg.sections,
        acknowledgments: cfg.acknowledgments,
        button_label: cfg.button_label,
        success_message: cfg.success_message,
        require_reacknowledge_on_version_change: cfg.require_reacknowledge_on_version_change,
      };
      const r = await axios.put(`${API}/admin/waitlist/briefing`, payload, { headers: auth });
      if (r.data.ok) {
        toast.success('Briefing saved.');
        load();
      }
    } catch (e) {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const updateSection = (idx, key, val) => {
    const next = { ...cfg, sections: [...cfg.sections] };
    next.sections[idx] = { ...next.sections[idx], [key]: val };
    setCfg(next);
  };

  const addSection = () =>
    setCfg({ ...cfg, sections: [...(cfg.sections || []), { title: 'New section', body: '' }] });

  const removeSection = (idx) =>
    setCfg({ ...cfg, sections: cfg.sections.filter((_, i) => i !== idx) });

  const updateAck = (idx, key, val) => {
    const next = { ...cfg, acknowledgments: [...cfg.acknowledgments] };
    next.acknowledgments[idx] = { ...next.acknowledgments[idx], [key]: val };
    setCfg(next);
  };

  const addAck = () =>
    setCfg({
      ...cfg,
      acknowledgments: [...(cfg.acknowledgments || []), { key: `ack_${Date.now()}`, label: '', required: true }],
    });

  const removeAck = (idx) =>
    setCfg({ ...cfg, acknowledgments: cfg.acknowledgments.filter((_, i) => i !== idx) });

  const downloadCsv = async () => {
    try {
      const res = await axios.get(`${API}/admin/waitlist/briefing/acknowledgments.csv`, {
        headers: auth,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'activation_briefing_acknowledgments.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
    } catch (e) {
      toast.error('CSV export failed');
    }
  };

  if (!cfg) {
    return <Card><CardContent className="p-6 text-sm text-muted-foreground">Loading…</CardContent></Card>;
  }

  return (
    <div className="space-y-4" data-testid="admin-briefing-manager">
      {/* Header w/ stats */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <FileText className="h-5 w-5 text-slate-600" />
          <div>
            <div className="font-semibold text-slate-800">Activation Briefing</div>
            <div className="text-xs text-slate-500">
              Current version <b>{cfg.version}</b>
              {stats && <> · <b>{stats.total_acks}</b> total acknowledgments</>}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant={view === 'edit' ? 'default' : 'outline'} size="sm" onClick={() => setView('edit')} data-testid="briefing-view-edit-btn">
            Edit
          </Button>
          <Button variant={view === 'acks' ? 'default' : 'outline'} size="sm" onClick={() => setView('acks')} data-testid="briefing-view-acks-btn">
            <Users className="h-3 w-3 mr-1" />Acknowledgments ({stats?.total_acks ?? 0})
          </Button>
          <Button variant="outline" size="sm" onClick={load}>
            <RefreshCw className="h-3 w-3 mr-1" />Reload
          </Button>
          {view === 'edit' && (
            <>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setPreviewOpen(true)}
                data-testid="briefing-preview-btn"
              >
                <Eye className="h-3 w-3 mr-1" />Preview as member
              </Button>
              <Button size="sm" onClick={save} disabled={saving} data-testid="briefing-save-btn">
                <Save className="h-3 w-3 mr-1" />{saving ? 'Saving…' : 'Save'}
              </Button>
            </>
          )}
        </div>
      </div>

      {view === 'edit' && (
        <Card>
          <CardContent className="p-5 space-y-4">
            <div className="grid md:grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-600">Version</label>
                <Input value={cfg.version} onChange={(e) => setCfg({ ...cfg, version: e.target.value })} data-testid="briefing-version-input" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Title</label>
                <Input value={cfg.title} onChange={(e) => setCfg({ ...cfg, title: e.target.value })} />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Subtitle</label>
                <Input value={cfg.subtitle || ''} onChange={(e) => setCfg({ ...cfg, subtitle: e.target.value })} />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Intro paragraph</label>
              <Textarea rows={3} value={cfg.intro || ''} onChange={(e) => setCfg({ ...cfg, intro: e.target.value })} />
            </div>

            <div className="flex items-center justify-between border rounded p-3">
              <div>
                <div className="font-medium text-sm">Require re-acknowledgment on version change</div>
                <div className="text-xs text-muted-foreground">
                  When ON, bumping the version forces every existing user to re-acknowledge to keep their link active.
                </div>
              </div>
              <Switch
                checked={!!cfg.require_reacknowledge_on_version_change}
                onCheckedChange={(v) => setCfg({ ...cfg, require_reacknowledge_on_version_change: v })}
                data-testid="briefing-reack-toggle"
              />
            </div>

            {/* Sections */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-medium text-slate-700">Briefing sections</div>
                <Button size="sm" variant="outline" onClick={addSection} data-testid="briefing-add-section-btn">
                  <Plus className="h-3 w-3 mr-1" />Add section
                </Button>
              </div>
              <div className="space-y-3">
                {(cfg.sections || []).map((s, i) => (
                  <div key={i} className="border rounded p-3 space-y-2" data-testid={`briefing-section-edit-${i}`}>
                    <div className="flex items-center justify-between gap-2">
                      <Input value={s.title} onChange={(e) => updateSection(i, 'title', e.target.value)} placeholder="Section title" />
                      <Button size="icon" variant="ghost" onClick={() => removeSection(i)} data-testid={`briefing-remove-section-${i}`}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                    <Textarea rows={3} value={s.body} onChange={(e) => updateSection(i, 'body', e.target.value)} placeholder="Section body" />
                  </div>
                ))}
              </div>
            </div>

            {/* Acknowledgments */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-medium text-slate-700">Acknowledgments (checkboxes)</div>
                <Button size="sm" variant="outline" onClick={addAck} data-testid="briefing-add-ack-btn">
                  <Plus className="h-3 w-3 mr-1" />Add acknowledgment
                </Button>
              </div>
              <div className="space-y-2">
                {(cfg.acknowledgments || []).map((a, i) => (
                  <div key={a.key} className="border rounded p-3 grid md:grid-cols-12 gap-2 items-start" data-testid={`briefing-ack-edit-${i}`}>
                    <div className="md:col-span-3">
                      <label className="text-[10px] uppercase text-slate-500">Key</label>
                      <Input value={a.key} onChange={(e) => updateAck(i, 'key', e.target.value)} className="text-xs font-mono" />
                    </div>
                    <div className="md:col-span-7">
                      <label className="text-[10px] uppercase text-slate-500">Label shown to user</label>
                      <Input value={a.label} onChange={(e) => updateAck(i, 'label', e.target.value)} />
                    </div>
                    <div className="md:col-span-1 flex flex-col items-center justify-end gap-1 pt-4">
                      <Switch
                        checked={a.required !== false}
                        onCheckedChange={(v) => updateAck(i, 'required', v)}
                      />
                      <span className="text-[10px] text-slate-500">required</span>
                    </div>
                    <div className="md:col-span-1 flex items-end justify-end pt-4">
                      <Button size="icon" variant="ghost" onClick={() => removeAck(i)} data-testid={`briefing-remove-ack-${i}`}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-600">Button label</label>
                <Input value={cfg.button_label || ''} onChange={(e) => setCfg({ ...cfg, button_label: e.target.value })} />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Success message</label>
                <Input value={cfg.success_message || ''} onChange={(e) => setCfg({ ...cfg, success_message: e.target.value })} />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {view === 'acks' && (
        <Card>
          <CardContent className="p-5">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Badge variant="outline">Total: {stats?.total_acks ?? 0}</Badge>
                {stats && Object.entries(stats.by_version || {}).map(([v, n]) => (
                  <Badge key={v} variant="secondary">{v}: {n}</Badge>
                ))}
              </div>
              <Button size="sm" variant="outline" onClick={downloadCsv} data-testid="briefing-csv-download-btn">
                <Download className="h-3 w-3 mr-1" />Download CSV
              </Button>
            </div>
            {acks.length === 0 ? (
              <div className="py-8 text-center text-muted-foreground text-sm">No acknowledgments yet.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="briefing-acks-table">
                  <thead>
                    <tr className="text-left border-b">
                      <th className="py-2">Name</th>
                      <th className="py-2">Email</th>
                      <th className="py-2">Signup ID</th>
                      <th className="py-2">Version</th>
                      <th className="py-2">Keys</th>
                      <th className="py-2">IP</th>
                      <th className="py-2">Accepted at</th>
                    </tr>
                  </thead>
                  <tbody>
                    {acks.map((a) => {
                      const fullName = `${a.signup_first_name || ''} ${a.signup_last_name || ''}`.trim();
                      return (
                        <tr key={a.id} className="border-b" data-testid={`briefing-ack-row-${a.id}`}>
                          <td className="py-2 whitespace-nowrap">{fullName || '—'}</td>
                          <td className="py-2">{a.signup_email || '—'}</td>
                          <td className="py-2 font-mono">{a.signup_id.slice(0, 12)}…</td>
                          <td className="py-2"><Badge variant="outline">{a.briefing_version}</Badge></td>
                          <td className="py-2 text-muted-foreground">{(a.accepted_keys || []).length}</td>
                          <td className="py-2 font-mono">{a.ip_address || '—'}</td>
                          <td className="py-2 whitespace-nowrap">{new Date(a.created_at).toLocaleString()}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Preview as member dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="briefing-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-4 w-4 text-indigo-600" /> Preview as member
            </DialogTitle>
            <DialogDescription>
              Live preview of the current unsaved draft. This is what a waitlist user sees inside the limited dashboard. Nothing is saved or recorded from here.
            </DialogDescription>
          </DialogHeader>
          {cfg && (
            <ActivationBriefingCard previewConfig={cfg} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BriefingManagerTab;
